import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Baby,
  Trash2,
  Heart,
  Check,
  ChevronLeft,
  Loader2,
  Lock,
  PartyPopper,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import { Input } from '@/components/ui';
import { BabyData, BabyInfo } from '@/store/pregnancyStore';
import { Link } from 'react-router-dom';
import { useLimits } from '@/hooks/useLimits';
import { useTranslation } from '@parentpath/i18n';

type CompletionFlow = 'select' | 'birth' | 'birth-form' | 'birth-success' | 'end-help' | 'end-confirm' | 'end-premium';

interface CompletePregnancyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleteBirth: (
    birthDate: string,
    birthTime?: string,
    babyData?: BabyData
  ) => Promise<boolean>;
  onDelete: () => Promise<void>;
  childCount: number;
  babyNames: Array<{ name: string; gender: string }>;
  isPremium: boolean;
  premiumExpiresAt?: string | null;
  onManageSubscription?: () => Promise<void>;
}

export function CompletePregnancyModal({
  isOpen,
  onClose,
  onCompleteBirth,
  onDelete,
  childCount,
  babyNames,
  isPremium,
  premiumExpiresAt,
  onManageSubscription,
}: CompletePregnancyModalProps) {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const { getNumberLimit } = useLimits();
  const confirmationText = t('profile:modals.completePregnancy.confirmText');
  const babyNamesLimit = getNumberLimit('baby_names_assign');
  const canAssignNames = babyNamesLimit > 0;

  const [flow, setFlow] = useState<CompletionFlow>('select');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Birth form state
  const [birthDate, setBirthDate] = useState(new Date().toISOString().split('T')[0]);
  const [birthTime, setBirthTime] = useState('');
  const [babies, setBabies] = useState<BabyInfo[]>(() =>
    Array(childCount)
      .fill(null)
      .map((_, i) => ({
        name: babyNames[i]?.name || '',
        gender: (babyNames[i]?.gender as 'female' | 'male' | 'diverse') || undefined,
        weight_grams: undefined,
        height_cm: undefined,
      }))
  );

  // End confirmation
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const resetState = () => {
    setFlow('select');
    setIsLoading(false);
    setError(null);
    setBirthDate(new Date().toISOString().split('T')[0]);
    setBirthTime('');
    setBabies(
      Array(childCount)
        .fill(null)
        .map((_, i) => ({
          name: babyNames[i]?.name || '',
          gender: (babyNames[i]?.gender as 'female' | 'male' | 'diverse') || undefined,
          weight_grams: undefined,
          height_cm: undefined,
        }))
    );
    setConfirmText('');
  };

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (flow === 'birth-form') {
      setFlow('birth');
    } else if (flow === 'end-confirm') {
      setFlow('end-help');
    } else if (flow === 'end-premium') {
      setFlow('select');
    } else if (flow === 'end-help') {
      setFlow('select');
    } else {
      setFlow('select');
    }
    setError(null);
  };

  const handleCompleteBirth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const babyData: BabyData = {
        babies: babies.map((b) => ({
          name: canAssignNames ? (b.name || undefined) : undefined,
          gender: b.gender,
          weight_grams: b.weight_grams,
          height_cm: b.height_cm,
        })),
      };

      const success = await onCompleteBirth(
        birthDate,
        birthTime || undefined,
        babyData
      );

      if (success) {
        setFlow('birth-success');

        // Auto close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setError(t('profile:modals.completePregnancy.errorComplete'));
      }
    } catch (err) {
      console.error('Error completing birth:', err);
      setError(t('profile:modals.completePregnancy.errorGeneral'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndPregnancy = async () => {
    if (confirmText !== confirmationText) return;

    setIsLoading(true);
    setError(null);

    try {
      await onDelete();
      handleClose();
    } catch (err) {
      console.error('Error ending pregnancy:', err);
      setError(t('profile:modals.completePregnancy.errorEnd'));
      setIsLoading(false);
    }
  };

  const updateBaby = (index: number, updates: Partial<BabyInfo>) => {
    setBabies((prev) =>
      prev.map((b, i) => (i === index ? { ...b, ...updates } : b))
    );
  };

  const addBaby = () => {
    setBabies((prev) => [...prev, { name: '', gender: undefined }]);
  };

  // Render based on current flow
  const renderContent = () => {
    switch (flow) {
      case 'select':
        return renderSelectFlow();
      case 'birth':
        return renderBirthInfoFlow();
      case 'birth-form':
        return renderBirthFormFlow();
      case 'birth-success':
        return renderBirthSuccessFlow();
      case 'end-help':
        return renderEndHelpFlow();
      case 'end-confirm':
        return renderEndConfirmFlow();
      case 'end-premium':
        return renderEndPremiumFlow();
      default:
        return null;
    }
  };

  // FLOW: Selection (now only 2 options)
  const renderSelectFlow = () => (
    <>
      <div className="bottom-sheet-header">
        <h2>{t('profile:modals.completePregnancy.title')}</h2>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-3">
        <p className="text-white/70 mb-4">{t('profile:modals.completePregnancy.howToContinue')}</p>

        <button
          onClick={() => setFlow('birth')}
          className="w-full p-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-full bg-pp-green/20 flex items-center justify-center">
            <Baby className="w-6 h-6 text-pp-green" />
          </div>
          <div>
            <p className="font-medium text-white">{t('profile:modals.completePregnancy.babyBorn')}</p>
            <p className="text-sm text-white/60">
              {t('profile:modals.completePregnancy.babyBornDesc')}
            </p>
          </div>
        </button>

        <button
          onClick={() => isPremium ? setFlow('end-premium') : setFlow('end-help')}
          className="w-full p-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <p className="font-medium text-white">{t('profile:modals.completePregnancy.endPregnancy')}</p>
            <p className="text-sm text-white/60">
              {t('profile:modals.completePregnancy.endPregnancyDesc')}
            </p>
          </div>
        </button>
      </div>

      <div className="bottom-sheet-footer">
        <button
          onClick={handleClose}
          className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
        >
          {t('common:cancel')}
        </button>
      </div>
    </>
  );

  // FLOW A: Birth Info
  const renderBirthInfoFlow = () => (
    <>
      <div className="bottom-sheet-header justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-left">{t('profile:modals.completePregnancy.title')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Deactivated Features */}
        <div>
          <p className="text-white/70 mb-2">
            {t('profile:modals.completePregnancy.deactivatedFeatures')}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-white">
              <X className="w-4 h-4 text-white/50" />
              <span>{t('profile:modals.completePregnancy.featureSswTracker')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <X className="w-4 h-4 text-white/50" />
              <span>{t('profile:modals.completePregnancy.featureNameFinder')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <X className="w-4 h-4 text-white/50" />
              <span>{t('profile:modals.completePregnancy.featureContractionTracker')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <X className="w-4 h-4 text-white/50" />
              <span>{t('profile:modals.completePregnancy.featurePregnancyContent')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <X className="w-4 h-4 text-white/50" />
              <span>{t('profile:modals.completePregnancy.featureProductCheck')}</span>
            </div>
          </div>
        </div>

        {/* New Features */}
        <div>
          <p className="text-white/70 mb-2">
            {t('profile:modals.completePregnancy.newFeatures')}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-pp-green" />
              <span>{t('profile:modals.completePregnancy.featureBabyDevelopment')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-pp-green" />
              <span>{t('profile:modals.completePregnancy.featureBabyCare')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-pp-green" />
              <span>{t('profile:modals.completePregnancy.featureGrowthTracker')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-pp-green" />
              <span>{t('profile:modals.completePregnancy.featureSleepPlan')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-pp-green" />
              <span>{t('profile:modals.completePregnancy.featureAiContext')}</span>
            </div>
          </div>
        </div>

        {/* Preserved Features */}
        <div>
          <p className="text-white/70 mb-2">
            {t('profile:modals.completePregnancy.preservedFeatures')}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-white">
              <Check className="w-4 h-4 text-pp-orange" />
              <span>{t('profile:modals.completePregnancy.featurePartnerSync')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 pl-6">
              <span className="text-sm">{t('profile:modals.completePregnancy.inclCalendar')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 pl-6">
              <span className="text-sm">{t('profile:modals.completePregnancy.inclChecklists')}</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Check className="w-4 h-4 text-pp-orange" />
              <span>{t('profile:modals.completePregnancy.featureInfoCard')} <span className="text-white/50 text-sm">({t('profile:modals.completePregnancy.babyDataExpansionPlanned')})</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-sheet-footer">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
        >
          {t('common:cancel')}
        </button>
        <button
          onClick={() => setFlow('birth-form')}
          className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
        >
          {t('profile:modals.completePregnancy.next')}
        </button>
      </div>
    </>
  );

  // FLOW A: Birth Form
  const renderBirthFormFlow = () => (
    <>
      <div className="bottom-sheet-header justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-left">{t('profile:modals.completePregnancy.birthFormTitle')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Birth date & time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('profile:modals.completePregnancy.birthDate')}*
            </label>
            <Input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full bg-white/10 border-white/20 text-white"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('profile:modals.completePregnancy.birthTime')}
            </label>
            <Input
              type="time"
              value={birthTime}
              onChange={(e) => setBirthTime(e.target.value)}
              className="w-full bg-white/10 border-white/20 text-white"
              placeholder="HH:MM"
            />
          </div>
        </div>

        <div className="border-t border-white/10 pt-4">
          <p className="text-sm font-medium text-white/70 mb-3">
            {t('profile:modals.completePregnancy.babyDataOptional')}
          </p>

          {babies.map((baby, index) => (
            <div
              key={index}
              className="bg-white/5 rounded-xl p-4 mb-3 space-y-3"
            >
              {babies.length > 1 && (
                <p className="text-sm font-medium text-pp-orange">
                  {t('profile:modals.completePregnancy.babyIndex', { index: index + 1 })}
                </p>
              )}

              {canAssignNames ? (
                <div>
                  <label className="block text-xs text-white/60 mb-1">{t('profile:modals.completePregnancy.nameLabel')}</label>
                  <Input
                    value={baby.name || ''}
                    onChange={(e) => updateBaby(index, { name: e.target.value })}
                    placeholder={t('profile:modals.completePregnancy.namePlaceholder')}
                    className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 py-2">
                  <Lock className="w-4 h-4 text-pp-orange flex-shrink-0" />
                  <p className="text-xs text-white/50">
                    <Link to="/subscription" className="text-pp-orange hover:underline" onClick={handleClose}>
                      Premium
                    </Link>
                    {' '}{t('profile:modals.completePregnancy.premiumNameRequired')}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs text-white/60 mb-1">
                  {t('profile:modals.completePregnancy.genderLabel')}
                </label>
                <div className="flex gap-2">
                  {(['female', 'male', 'diverse'] as const).map((g) => (
                    <button
                      key={g}
                      onClick={() => updateBaby(index, { gender: g })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        baby.gender === g
                          ? 'bg-pp-orange text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/15'
                      }`}
                    >
                      {g === 'female' ? t('profile:modals.completePregnancy.genderFemale') : g === 'male' ? t('profile:modals.completePregnancy.genderMale') : t('profile:modals.completePregnancy.genderDiverse')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">
                    {t('profile:modals.completePregnancy.weightLabel')}
                  </label>
                  <Input
                    type="number"
                    value={baby.weight_grams || ''}
                    onChange={(e) =>
                      updateBaby(index, {
                        weight_grams: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder={t('profile:modals.completePregnancy.weightPlaceholder')}
                    className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">
                    {t('profile:modals.completePregnancy.heightLabel')}
                  </label>
                  <Input
                    type="number"
                    value={baby.height_cm || ''}
                    onChange={(e) =>
                      updateBaby(index, {
                        height_cm: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder={t('profile:modals.completePregnancy.heightPlaceholder')}
                    className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
              </div>
            </div>
          ))}

          {babies.length < 4 && (
            <button
              onClick={addBaby}
              className="w-full py-2 rounded-lg bg-white/10 text-white/70 text-sm hover:bg-white/15"
            >
              + {t('profile:modals.completePregnancy.addBaby')}
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-xl">
            {error}
          </div>
        )}
      </div>

      <div className="bottom-sheet-footer">
        <button
          onClick={handleBack}
          disabled={isLoading}
          className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
        >
          {t('profile:modals.completePregnancy.back')}
        </button>
        <button
          onClick={handleCompleteBirth}
          disabled={isLoading || !birthDate}
          className="flex-1 py-2.5 rounded-xl bg-pp-green text-white font-medium hover:bg-pp-green/90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <PartyPopper className="w-4 h-4" />
              {t('common:save')}
            </>
          )}
        </button>
      </div>
    </>
  );

  // FLOW A: Birth Success
  const renderBirthSuccessFlow = () => (
    <div className="py-12 text-center">
      <div className="w-20 h-20 mx-auto mb-6 bg-pp-green/20 rounded-full flex items-center justify-center">
        <PartyPopper className="w-10 h-10 text-pp-green" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        {t('profile:modals.completePregnancy.successTitle')}
      </h2>
      <p className="text-white/70">
        {t('profile:modals.completePregnancy.successMessage')}
      </p>
    </div>
  );

  // FLOW B: End - Help resources (shown before confirmation)
  const renderEndHelpFlow = () => (
    <>
      <div className="bottom-sheet-header justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-left">{t('profile:modals.completePregnancy.endTitle')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-4">
        <p className="text-white/70">
          {t('profile:modals.completePregnancy.helpIntro')}
        </p>

        {/* Help resources */}
        <div className="bg-pp-green/10 border border-pp-green/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-pp-green flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-pp-green mb-2">
                {t('profile:modals.completePregnancy.helpTitle')}
              </p>
              <ul className="text-sm text-white/70 space-y-1.5">
                <li>• {t('profile:modals.completePregnancy.helpHotline')}</li>
                <li>• www.initiative-regenbogen.de</li>
                <li>• www.sternenkinder-eltern.de</li>
              </ul>
            </div>
          </div>
        </div>

        {/* In-app help link */}
        <Link
          to="/help"
          onClick={handleClose}
          className="flex items-center gap-3 p-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10"
        >
          <div className="w-10 h-10 rounded-full bg-pp-orange/20 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-pp-orange" />
          </div>
          <div>
            <p className="font-medium text-white">{t('profile:modals.completePregnancy.helpFaq')}</p>
            <p className="text-sm text-white/60">{t('profile:modals.completePregnancy.helpFaqDesc')}</p>
          </div>
        </Link>

        <div className="bg-white/5 rounded-xl p-3 mt-4">
          <p className="text-sm text-white/70">
            {t('profile:modals.completePregnancy.endWarning')}
          </p>
        </div>
      </div>

      <div className="bottom-sheet-footer">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
        >
          {t('common:cancel')}
        </button>
        <button
          onClick={() => setFlow('end-confirm')}
          className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600"
        >
          {t('profile:modals.completePregnancy.continue')}
        </button>
      </div>
    </>
  );

  // FLOW B: End - Premium warning (for premium users)
  const renderEndPremiumFlow = () => (
    <>
      <div className="bottom-sheet-header justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-left">{t('profile:modals.completePregnancy.premiumTitle')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-4">
        <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-xl p-4">
          <p className="font-medium text-pp-orange mb-2">
            {t('profile:modals.completePregnancy.premiumActive')}
            {premiumExpiresAt && (
              <span> {t('profile:modals.completePregnancy.premiumUntil', { date: new Date(premiumExpiresAt).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US') })}</span>
            )}
            .
          </p>
          <p className="text-sm text-white/70">
            {t('profile:modals.completePregnancy.premiumEndWarning')}
          </p>
        </div>

        <p className="text-white/70">
          {t('profile:modals.completePregnancy.premiumCancelQuestion')}
        </p>
      </div>

      <div className="bottom-sheet-footer flex-col gap-2">
        {onManageSubscription && (
          <button
            onClick={onManageSubscription}
            className="w-full py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
          >
            {t('profile:modals.completePregnancy.manageSubscription')}
          </button>
        )}
        <button
          onClick={() => setFlow('end-help')}
          className="w-full py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
        >
          {t('profile:modals.completePregnancy.continueAnyway')}
        </button>
      </div>
    </>
  );

  // FLOW B: End - Confirmation
  const renderEndConfirmFlow = () => (
    <>
      <div className="bottom-sheet-header justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-left">{t('profile:modals.completePregnancy.confirmTitle')}</h2>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="bottom-sheet-content space-y-4">
        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
          <p className="font-medium text-red-400 mb-2">
            {t('profile:modals.completePregnancy.confirmIrreversible')}
          </p>
          <p className="text-sm text-white/70">{t('profile:modals.completePregnancy.confirmDataDeleted')}</p>
          <ul className="text-sm text-white/70 mt-2 space-y-1">
            <li>• {t('profile:modals.completePregnancy.confirmItem1')}</li>
            <li>• {t('profile:modals.completePregnancy.confirmItem2')}</li>
            <li>• {t('profile:modals.completePregnancy.confirmItem3')}</li>
            <li>• {t('profile:modals.completePregnancy.confirmItem4')}</li>
            <li>• {t('profile:modals.completePregnancy.confirmItem5')}</li>
          </ul>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('profile:modals.completePregnancy.confirmPrompt', { text: confirmationText })}
          </label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder={confirmationText}
            className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40"
            disabled={isLoading}
            autoComplete="off"
          />
        </div>

        {error && (
          <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-xl">
            {error}
          </div>
        )}
      </div>

      <div className="bottom-sheet-footer">
        <button
          onClick={handleBack}
          disabled={isLoading}
          className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
        >
          {t('common:cancel')}
        </button>
        <button
          onClick={handleEndPregnancy}
          disabled={confirmText !== confirmationText || isLoading}
          className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 disabled:bg-red-500/50 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Trash2 className="w-4 h-4" />
              {t('profile:modals.completePregnancy.confirmButton')}
            </>
          )}
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">{renderContent()}</div>
      </div>
    </>,
    document.body
  );
}
